import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    allDay: false,
    category: 'default'
  });

  // Dados de exemplo para eventos
  const sampleEvents = [
    {
      id: 1,
      title: 'DevOps Conference',
      description: 'Conferência anual de DevOps com palestras e workshops',
      startDate: '2025-03-15',
      startTime: '09:00',
      endDate: '2025-03-15',
      endTime: '18:00',
      allDay: true,
      category: 'conference'
    },
    {
      id: 2,
      title: 'Respond to customer inquiries',
      description: 'Responder e-mails e mensagens de clientes',
      startDate: '2025-03-10',
      startTime: '10:00',
      endDate: '2025-03-10',
      endTime: '12:00',
      allDay: false,
      category: 'task'
    },
    {
      id: 3,
      title: 'Conduct interviews',
      description: 'Entrevistas para a vaga de desenvolvedor frontend',
      startDate: '2025-03-10',
      startTime: '14:00',
      endDate: '2025-03-10',
      endTime: '17:00',
      allDay: false,
      category: 'meeting'
    },
    {
      id: 4,
      title: 'Follow-up with the client',
      description: 'Acompanhamento do projeto com o cliente',
      startDate: '2025-03-10',
      startTime: '09:00',
      endDate: '2025-03-10',
      endTime: '10:00',
      allDay: false,
      category: 'meeting'
    }
  ];

  useEffect(() => {
    // Simular carregamento de eventos da API
    setTimeout(() => {
      setEvents(sampleEvents);
      setLoading(false);
    }, 1000);
  }, []);

  // Função para formatar a data
  const formatDate = (date) => {
    const options = { month: 'short', day: 'numeric' };
    return new Date(date).toLocaleDateString('pt-BR', options);
  };

  // Função para obter o nome do mês atual
  const getCurrentMonth = () => {
    const options = { month: 'long', year: 'numeric' };
    return new Date(currentDate).toLocaleDateString('pt-BR', options);
  };

  // Função para gerar os dias da semana
  const generateWeekDays = () => {
    const days = [];
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day + (day === 0 ? -6 : 1)); // Ajustar para começar na segunda-feira

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }

    return days;
  };

  // Função para navegar entre semanas
  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  // Função para verificar se um evento ocorre em um determinado dia
  const eventOccursOnDay = (event, date) => {
    const eventDate = new Date(event.startDate);
    return eventDate.toDateString() === date.toDateString();
  };

  // Função para obter eventos de um dia específico
  const getEventsForDay = (date) => {
    return events.filter(event => eventOccursOnDay(event, date));
  };

  // Função para abrir o modal de detalhes do evento
  const openEventDetails = (event) => {
    setSelectedEvent(event);
  };

  // Função para abrir o modal de novo evento
  const openNewEventModal = () => {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    const formattedTime = `${today.getHours().toString().padStart(2, '0')}:${today.getMinutes().toString().padStart(2, '0')}`;
    
    setNewEvent({
      ...newEvent,
      startDate: formattedDate,
      startTime: formattedTime,
      endDate: formattedDate,
      endTime: formattedTime
    });
    
    setShowNewEventModal(true);
  };

  // Função para salvar um novo evento
  const saveNewEvent = () => {
    const eventToSave = {
      ...newEvent,
      id: events.length + 1
    };
    
    setEvents([...events, eventToSave]);
    setShowNewEventModal(false);
    toast.success('Evento criado com sucesso!');
    
    // Resetar o formulário
    setNewEvent({
      title: '',
      description: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      allDay: false,
      category: 'default'
    });
  };

  // Função para renderizar a cor do evento com base na categoria
  const getCategoryColor = (category) => {
    switch (category) {
      case 'meeting':
        return 'bg-blue';
      case 'task':
        return 'bg-green';
      case 'conference':
        return 'bg-purple';
      default:
        return 'bg-gray';
    }
  };

  // Função para renderizar o ícone da categoria
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'meeting':
        return <i className="fas fa-users"></i>;
      case 'task':
        return <i className="fas fa-tasks"></i>;
      case 'conference':
        return <i className="fas fa-microphone"></i>;
      default:
        return <i className="fas fa-calendar"></i>;
    }
  };

  return (
    <MainLayout>
      <div className="container-xl p-0">
        {/* Header simples com título e botões de ação */}
        <div className="d-flex align-items-center justify-content-between py-3 border-bottom">
          <div className="d-flex align-items-center">
            <h2 className="page-title mb-0 ms-3">Calendar</h2>
          </div>
          <div className="d-flex align-items-center me-3">
            <div className="d-flex align-items-center me-2">
              <span className="text-muted me-2">Sat</span>
              <span className="fs-2 fw-bold">15</span>
            </div>
            <div className="btn-group me-2">
              <button className={`btn ${viewMode === 'month' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setViewMode('month')}>
                month
              </button>
              <button className={`btn ${viewMode === 'week' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setViewMode('week')}>
                week
              </button>
              <button className={`btn ${viewMode === 'day' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setViewMode('day')}>
                day
              </button>
              <button className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setViewMode('list')}>
                list
              </button>
              <button className="btn btn-outline-primary">All</button>
            </div>
            <div className="me-2">
              <button className="btn btn-outline-secondary" onClick={() => setCurrentDate(new Date())}>
                Today
              </button>
            </div>
            <div className="me-2">
              <button className="btn btn-primary" onClick={openNewEventModal}>
                Create
              </button>
            </div>
            <div className="d-flex align-items-center">
              <div className="position-relative me-2">
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                  10
                </span>
                <button className="btn btn-icon btn-outline-secondary">
                  <i className="far fa-envelope"></i>
                </button>
              </div>
              <div className="position-relative me-2">
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                  21
                </span>
                <button className="btn btn-icon btn-outline-secondary">
                  <i className="far fa-comment-alt"></i>
                </button>
              </div>
              <div className="position-relative">
                <button className="btn btn-icon btn-outline-secondary">
                  <i className="far fa-bell"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Layout do calendário como na imagem */}
        <div className="calendar-container">
          <div className="row g-0">
            {/* Coluna do mês */}
            <div className="col-auto calendar-month-column border-end" style={{ width: '250px' }}>
              <div className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h3 className="h5 mb-0">March 2025</h3>
                  <div className="btn-group btn-group-sm">
                    <button className="btn btn-outline-secondary">
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button className="btn btn-outline-secondary">
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
                
                {/* Dias da semana */}
                <div className="calendar-weekdays d-flex mb-2">
                  <div className="calendar-weekday">Mo</div>
                  <div className="calendar-weekday">Tu</div>
                  <div className="calendar-weekday">We</div>
                  <div className="calendar-weekday">Th</div>
                  <div className="calendar-weekday">Fr</div>
                  <div className="calendar-weekday">Sa</div>
                  <div className="calendar-weekday">Su</div>
                </div>
                
                {/* Dias do mês */}
                <div className="calendar-days">
                  <div className="calendar-day text-muted">24</div>
                  <div className="calendar-day text-muted">25</div>
                  <div className="calendar-day text-muted">26</div>
                  <div className="calendar-day text-muted">27</div>
                  <div className="calendar-day text-muted">28</div>
                  <div className="calendar-day">1</div>
                  <div className="calendar-day">2</div>
                  
                  <div className="calendar-day">3</div>
                  <div className="calendar-day">4</div>
                  <div className="calendar-day">5</div>
                  <div className="calendar-day">6</div>
                  <div className="calendar-day">7</div>
                  <div className="calendar-day">8</div>
                  <div className="calendar-day">9</div>
                  
                  <div className="calendar-day">10</div>
                  <div className="calendar-day">11</div>
                  <div className="calendar-day">12</div>
                  <div className="calendar-day">13</div>
                  <div className="calendar-day">14</div>
                  <div className="calendar-day active">15</div>
                  <div className="calendar-day">16</div>
                  
                  <div className="calendar-day">17</div>
                  <div className="calendar-day">18</div>
                  <div className="calendar-day">19</div>
                  <div className="calendar-day">20</div>
                  <div className="calendar-day">21</div>
                  <div className="calendar-day">22</div>
                  <div className="calendar-day">23</div>
                  
                  <div className="calendar-day">24</div>
                  <div className="calendar-day">25</div>
                  <div className="calendar-day">26</div>
                  <div className="calendar-day">27</div>
                  <div className="calendar-day">28</div>
                  <div className="calendar-day">29</div>
                  <div className="calendar-day">30</div>
                  
                  <div className="calendar-day">31</div>
                  <div className="calendar-day text-muted">1</div>
                  <div className="calendar-day text-muted">2</div>
                  <div className="calendar-day text-muted">3</div>
                  <div className="calendar-day text-muted">4</div>
                  <div className="calendar-day text-muted">5</div>
                  <div className="calendar-day text-muted">6</div>
                </div>
              </div>
              
              {/* Meus Calendários */}
              <div className="calendar-lists p-3 border-top">
                <div className="mb-3">
                  <h4 className="h6 mb-2">My Calendars</h4>
                  <div className="calendar-list">
                    <div className="calendar-list-item">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked onChange={() => {}} />
                        <label className="form-check-label text-primary">Caesar</label>
                      </div>
                    </div>
                    <div className="calendar-list-item">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked onChange={() => {}} />
                        <label className="form-check-label text-danger">My Tasks</label>
                      </div>
                    </div>
                    <div className="calendar-list-item">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked onChange={() => {}} />
                        <label className="form-check-label text-info">Other tasks</label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="h6 mb-2">Public Calendars</h4>
                  <div className="calendar-list">
                    <div className="calendar-list-item">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked onChange={() => {}} />
                        <label className="form-check-label text-success">Company Calendar</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Coluna dos dias da semana */}
            <div className="col calendar-week-column">
              {loading ? (
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status"></div>
                  <p className="mt-3">Carregando eventos...</p>
                </div>
              ) : (
                <div className="calendar-week">
                  {/* Cabeçalho dos dias da semana */}
                  <div className="calendar-week-header">
                    <div className="row g-0">
                      <div className="col">
                        <div className="calendar-day-header">
                          <div className="day-name">Mon 10</div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-day-header">
                          <div className="day-name">Tue 11</div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-day-header">
                          <div className="day-name">Wed 12</div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-day-header">
                          <div className="day-name">Thu 13</div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-day-header">
                          <div className="day-name">Fri 14</div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-day-header active">
                          <div className="day-name">Sat 15</div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-day-header">
                          <div className="day-name">Sun 16</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Eventos all-day */}
                  <div className="calendar-all-day-events">
                    <div className="row g-0">
                      <div className="col">
                        <div className="calendar-event-container">
                          <div className="calendar-event bg-blue text-white">Respond to customer inquiries</div>
                          <div className="calendar-event bg-blue text-white">Conduct interviews</div>
                          <div className="calendar-event bg-blue text-white">DevOps Conference</div>
                          <div className="calendar-event bg-blue text-white">Follow-up with the client</div>
                          <div className="text-end">
                            <span className="badge bg-secondary">+2 more</span>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-event-container">
                          <div className="text-end">
                            <span className="badge bg-secondary">+2 more</span>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-event-container">
                          <div className="text-end">
                            <span className="badge bg-secondary">+2 more</span>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-event-container">
                          <div className="text-end">
                            <span className="badge bg-secondary">+2 more</span>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-event-container">
                          <div className="text-end">
                            <span className="badge bg-secondary">+2 more</span>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-event-container">
                          <div className="calendar-event bg-purple text-white">DevOps Conference</div>
                          <div className="text-end">
                            <span className="badge bg-secondary">+2 more</span>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="calendar-event-container">
                          <div className="text-end">
                            <span className="badge bg-secondary">+2 more</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Slots de tempo */}
                  <div className="calendar-time-slots">
                    {Array.from({ length: 12 }).map((_, hourIndex) => {
                      const hour = 6 + hourIndex; // Começando às 6h
                      return (
                        <div key={hourIndex} className="calendar-time-slot">
                          <div className="row g-0">
                            <div className="col-auto calendar-time-label">
                              {hour}:00 {hour < 12 ? 'am' : 'pm'}
                            </div>
                            <div className="col">
                              <div className="row g-0 h-100">
                                <div className="col">
                                  <div className="calendar-cell"></div>
                                </div>
                                <div className="col">
                                  <div className="calendar-cell"></div>
                                </div>
                                <div className="col">
                                  <div className="calendar-cell"></div>
                                </div>
                                <div className="col">
                                  <div className="calendar-cell"></div>
                                </div>
                                <div className="col">
                                  <div className="calendar-cell"></div>
                                </div>
                                <div className="col">
                                  <div className="calendar-cell"></div>
                                </div>
                                <div className="col">
                                  <div className="calendar-cell"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de detalhes do evento */}
      {selectedEvent && (
        <div className="modal modal-blur fade show" id="modal-event-details" tabIndex="-1" style={{ display: 'block', paddingRight: '15px' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedEvent.title}</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedEvent(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className={`avatar ${getCategoryColor(selectedEvent.category)} me-2`}>
                      {getCategoryIcon(selectedEvent.category)}
                    </div>
                    <div>
                      <h4 className="mb-0">{selectedEvent.title}</h4>
                      <div className="text-muted">
                        {formatDate(selectedEvent.startDate)} {selectedEvent.allDay ? '(All day)' : `${selectedEvent.startTime} - ${selectedEvent.endTime}`}
                      </div>
                    </div>
                  </div>
                  <p>{selectedEvent.description}</p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-danger me-auto">
                  <i className="fas fa-trash me-2"></i>
                  Delete
                </button>
                <button type="button" className="btn btn-outline-primary">
                  <i className="fas fa-edit me-2"></i>
                  Edit
                </button>
                <button type="button" className="btn btn-primary" onClick={() => setSelectedEvent(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de novo evento */}
      {showNewEventModal && (
        <div className="modal modal-blur fade show" id="modal-new-event" tabIndex="-1" style={{ display: 'block', paddingRight: '15px' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Event</h5>
                <button type="button" className="btn-close" onClick={() => setShowNewEventModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    placeholder="Event title"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea 
                    className="form-control" 
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    placeholder="Event description"
                    rows="3"
                  ></textarea>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Start Date</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={newEvent.startDate}
                      onChange={(e) => setNewEvent({...newEvent, startDate: e.target.value})}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Start Time</label>
                    <input 
                      type="time" 
                      className="form-control" 
                      value={newEvent.startTime}
                      onChange={(e) => setNewEvent({...newEvent, startTime: e.target.value})}
                      disabled={newEvent.allDay}
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">End Date</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={newEvent.endDate}
                      onChange={(e) => setNewEvent({...newEvent, endDate: e.target.value})}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">End Time</label>
                    <input 
                      type="time" 
                      className="form-control" 
                      value={newEvent.endTime}
                      onChange={(e) => setNewEvent({...newEvent, endTime: e.target.value})}
                      disabled={newEvent.allDay}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      checked={newEvent.allDay}
                      onChange={(e) => setNewEvent({...newEvent, allDay: e.target.checked})}
                    />
                    <label className="form-check-label">
                      All Day
                    </label>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Category</label>
                  <select 
                    className="form-select" 
                    value={newEvent.category}
                    onChange={(e) => setNewEvent({...newEvent, category: e.target.value})}
                  >
                    <option value="default">Default</option>
                    <option value="meeting">Meeting</option>
                    <option value="task">Task</option>
                    <option value="conference">Conference</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setShowNewEventModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary ms-auto" onClick={saveNewEvent}>
                  <i className="fas fa-save me-2"></i>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overlay para modais */}
      {(selectedEvent || showNewEventModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </MainLayout>
  );
};

export default Calendar; 